import { useReactiveVar } from '@apollo/client'
import {
  AudienceType,
  BadgeAudience,
  Grid,
  IconButton,
  IconColor,
  IconName,
  IconSize,
  Menu,
  MenuOption,
  MenuVariant
} from '@focaldata/cin-ui-components'
import debounce from 'lodash/debounce'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { TextHighlight, TextSize, TextWeight } from '../../../constants/text'
import { usePasteCustomCriterionV2 } from '../../../hooks/audience/pasteCustomCriterionV2'
import { IsNonManualUIChange } from '../../../hooks/copyPaste/useCopyPasteComplete'
import CardTitle from '../../Questionnaire/CardTitle/CardTitle.control'
import useStyles from './AudienceQuestionCardHeader.styles'

interface Props {
  questionLk?: string
  defaultTitle: string
  hasError?: boolean
  isTitleEditable?: boolean
  canDelete?: boolean
  placeholder?: string
  quotaName?: string
  shouldTitleInputFocus?: boolean
  isStandardAudienceSingleChoice?: boolean
  isCustomAudienceSingleChoice?: boolean
  onChange?: (inputText: string) => void
  onClickLeftIcon?: () => void
  onSwitchQuestionType?: (isSingleSelect: boolean) => void
  isFdchatProject?: boolean
  hasDisqualifyingOption?: boolean
  actionContent?: JSX.Element
  logicContent?: JSX.Element
  // Global custom audience question shown read-only in a non-base market: keep
  // the Screening badge (it's still a screening question) instead of letting
  // read-only fall through to the misleading Targeting badge.
  linkedToBase?: boolean
}

const AudienceQuestionCardHeader: React.FC<Props> = (props: Props) => {
  const {
    questionLk = '',
    defaultTitle,
    hasError = false,
    isTitleEditable = true,
    canDelete = true,
    placeholder,
    quotaName,
    shouldTitleInputFocus,
    isStandardAudienceSingleChoice,
    isCustomAudienceSingleChoice,
    onChange,
    onClickLeftIcon,
    onSwitchQuestionType,
    isFdchatProject,
    hasDisqualifyingOption,
    actionContent,
    logicContent,
    linkedToBase = false
  } = props

  const [titleValue, setTitleValue] = useState<string>(defaultTitle)
  const isAfterPaste = useReactiveVar<boolean>(IsNonManualUIChange)

  useEffect(() => {
    if (isAfterPaste) {
      setTitleValue(defaultTitle)
      IsNonManualUIChange(false)
    }
  }, [defaultTitle, isAfterPaste])

  const handleOnChangeDebounced = useMemo(
    () =>
      debounce((titleValue: string) => {
        onChange?.(titleValue)
      }, 1000),
    [onChange]
  )

  const handleOnChange = useCallback(
    (title: string) => {
      if (!onChange) {
        return
      }
      setTitleValue(title)
      handleOnChangeDebounced(title)
    },
    [handleOnChangeDebounced, onChange]
  )

  const pasteInCustomCriterionText = usePasteCustomCriterionV2(
    questionLk,
    titleValue,
    handleOnChange
  )

  const { classes } = useStyles()

  const simpleMenu: MenuOption[] = [
    {
      id: 0,
      options: [
        {
          text: 'Single select',
          onClick: () => {
            if (onSwitchQuestionType) {
              onSwitchQuestionType(true)
            }
          },
          iconName: IconName.SingleChoice
        },
        {
          text: 'Multi select',
          onClick: () => {
            if (onSwitchQuestionType) {
              onSwitchQuestionType(false)
            }
          },
          iconName: IconName.MultipleChoice
        }
      ]
    }
  ]

  const selectTypeLabel = isCustomAudienceSingleChoice
    ? 'Single select'
    : 'Multi select'

  const getEntryTypeName: (
    isTitleEditable: boolean,
    quotaName: string | undefined
  ) => JSX.Element | string | undefined = (isTitleEditable, quotaName) => {
    if (!isTitleEditable) {
      // Read-only global question: keep the single/multi type visible as a
      // static label (the type can't be switched outside the base market, but
      // it should still be shown next to the Screening badge).
      return linkedToBase ? selectTypeLabel : quotaName
    }
    return (
      <Menu
        menuOptions={simpleMenu}
        title={selectTypeLabel}
        titleWidth={90}
        variant={MenuVariant.QuestionType}
      />
    )
  }

  const getBadgeAudienceQuestion = (isTitleEditable: boolean) => {
    // A global question locked in a non-base market is still a screening
    // question, so keep the Screening badge (read-only would otherwise fall
    // through to the misleading "Targeting" badge below). The tooltip explains
    // it's managed in the base market.
    if (linkedToBase) {
      return (
        <BadgeAudience
          badgeType={AudienceType.Screening}
          tooltipText="Global screening question — its wording and answer options are managed in the base market. You can still edit qualification and quotas here."
        />
      )
    }

    if (isFdchatProject) {
      // if it's an fdchat project and has a disqualifying option, show the screening badge
      // else show no badge
      if (hasDisqualifyingOption) {
        return <BadgeAudience badgeType={AudienceType.Screening} />
      } else {
        return
      }
    }

    if (isTitleEditable) {
      return (
        <BadgeAudience
          badgeType={AudienceType.Screening}
          tooltipText="Qualify people out before reaching your survey. Toggle on quotas if you need a representative sample. When the project is complete, you can use any screening question as a filter or cross-break for your data."
        />
      )
    }
    if (isStandardAudienceSingleChoice) {
      return (
        <BadgeAudience
          badgeType={AudienceType.Targeting}
          tooltipText="Use the tick boxes to pick which groups to target or exclude. Toggle on quotas where available if you need a representative sample."
        />
      )
    }
    return (
      <BadgeAudience
        badgeType={AudienceType.Targeting}
        tooltipText="Use the tick boxes to pick which groups to target or exclude. Toggle on quotas where available if you need a representative sample."
      />
    )
  }

  const placeholderText = placeholder || 'Type your question'
  const entryTypeName = getEntryTypeName(isTitleEditable, quotaName)
  const badgeAudienceQuestion = getBadgeAudienceQuestion(isTitleEditable)

  return (
    <Grid container>
      <Grid container item xs={9} direction="row" alignItems="center">
        <Grid item xs="auto" className={classes.questionType}>
          <p
            style={{
              color: hasError ? TextHighlight.Error : TextHighlight.Background
            }}
          >
            {entryTypeName}
          </p>
        </Grid>
        <Grid item>{badgeAudienceQuestion}</Grid>
      </Grid>
      <Grid
        container
        item
        xs={3}
        alignItems="center"
        direction="row"
        justifyContent="flex-end"
      >
        <Grid item xs="auto" className={classes.middleIcon}>
          {canDelete && (
            <IconButton
              iconName={IconName.DeleteOutline}
              iconSize={IconSize.Large}
              iconColor={IconColor.Background}
              onClick={onClickLeftIcon}
              negativePadding
              // Matches the KebabMenu trigger beside it, whose ripple is otherwise a different size.
              size="small"
              ariaLabel="Audience question delete"
            />
          )}
        </Grid>
        {/* Last, as on questionnaire cards. Not gated on canDelete: display logic stays editable where a
            global question's structure is locked. */}
        <Grid item xs="auto" className={classes.middleIcon}>
          {actionContent}
        </Grid>
      </Grid>

      {logicContent}

      <Grid container>
        {isTitleEditable && (
          <CardTitle
            titleValue={titleValue}
            ariaLabel="Custom audience input"
            shouldTitleInputFocus={shouldTitleInputFocus}
            hasError={!titleValue && hasError}
            placeholder={placeholderText}
            onChange={handleOnChange}
            onPaste={pasteInCustomCriterionText}
          />
        )}
        {!isTitleEditable && (
          <p
            style={{
              fontSize: TextSize.m,
              fontWeight: TextWeight.SemiBold,
              color: TextHighlight.Background
            }}
          >
            {/* A read-only title is driven entirely by the prop (the
                market-language overlay / language switch), so render it directly
                rather than the local titleValue, which is only seeded on mount
                and re-synced on paste and would otherwise ignore a language
                change. */}
            {defaultTitle}
          </p>
        )}
      </Grid>
    </Grid>
  )
}

export default memo(AudienceQuestionCardHeader)
